import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft,
  Building2,
  Tag,
  Gift,
  Save,
  Plus,
  Trash2,
  Loader2,
  Users,
  Search,
  CheckCircle2,
  XCircle,
  Percent,
  Euro,
  Calendar,
  Hash
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export default function AdminBillingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // TIREO Settings
  const [tireoSettings, setTireoSettings] = useState({
    company_name: "TIREO",
    address: "",
    postal_code: "",
    city: "",
    country: "France",
    siret: "",
    tva_number: "",
    email: "facturation@tireo.app",
    phone: ""
  });
  
  // Promo Codes
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [newPromoCode, setNewPromoCode] = useState({
    code: "",
    discount_type: "percent",
    discount_percent: 10,
    discount_amount: 0,
    free_operations: 0,
    valid_until: "",
    max_uses: "",
    plan_types: ["monthly", "annual", "premium", "single"]
  });
  
  // Users for credits
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditsToGrant, setCreditsToGrant] = useState(1);
  const [creditsReason, setCreditsReason] = useState("");

  useEffect(() => {
    if (user && user.email !== "admin@tireo.app") {
      toast.error("Accès réservé aux administrateurs");
      navigate("/dashboard");
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [settingsRes, codesRes, usersRes] = await Promise.all([
        api.get("/admin/tireo-settings").catch(() => ({ data: tireoSettings })),
        api.get("/admin/promo-codes").catch(() => ({ data: [] })),
        api.get("/admin/users").catch(() => ({ data: [] }))
      ]);
      
      if (settingsRes.data) setTireoSettings(settingsRes.data);
      setPromoCodes(codesRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error("Error loading admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveTireoSettings = async () => {
    setSaving(true);
    try {
      await api.put("/admin/tireo-settings", tireoSettings);
      toast.success("Paramètres TIREO enregistrés");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const createPromoCode = async () => {
    if (!newPromoCode.code) {
      toast.error("Le code est requis");
      return;
    }
    
    try {
      const payload = {
        code: newPromoCode.code,
        discount_percent: newPromoCode.discount_type === "percent" ? newPromoCode.discount_percent : null,
        discount_amount: newPromoCode.discount_type === "amount" ? newPromoCode.discount_amount : null,
        free_operations: newPromoCode.discount_type === "operations" ? newPromoCode.free_operations : null,
        valid_until: newPromoCode.valid_until || null,
        max_uses: newPromoCode.max_uses ? parseInt(newPromoCode.max_uses) : null,
        plan_types: newPromoCode.plan_types
      };
      
      const res = await api.post("/admin/promo-codes", payload);
      setPromoCodes([res.data, ...promoCodes]);
      setShowPromoModal(false);
      setNewPromoCode({
        code: "",
        discount_type: "percent",
        discount_percent: 10,
        discount_amount: 0,
        free_operations: 0,
        valid_until: "",
        max_uses: "",
        plan_types: ["monthly", "annual", "premium", "single"]
      });
      toast.success("Code promo créé");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const togglePromoCode = async (codeId, isActive) => {
    try {
      await api.put(`/admin/promo-codes/${codeId}?is_active=${!isActive}`);
      setPromoCodes(promoCodes.map(c => c.id === codeId ? { ...c, is_active: !isActive } : c));
      toast.success(isActive ? "Code désactivé" : "Code activé");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const grantCredits = async () => {
    if (!selectedUser) return;
    
    try {
      await api.post("/admin/grant-credits", {
        user_id: selectedUser.id,
        free_operations: creditsToGrant,
        reason: creditsReason
      });
      toast.success(`${creditsToGrant} opération(s) gratuite(s) accordée(s)`);
      setShowCreditsModal(false);
      setSelectedUser(null);
      setCreditsToGrant(1);
      setCreditsReason("");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'attribution");
    }
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.company_name?.toLowerCase().includes(userSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-gradient-to-r from-red-600 to-rose-600 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} className="text-white hover:bg-white/20">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>Gestion Facturation</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Tabs defaultValue="tireo" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="tireo" className="gap-2">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Infos TIREO</span>
            </TabsTrigger>
            <TabsTrigger value="promos" className="gap-2">
              <Tag className="w-4 h-4" />
              <span className="hidden sm:inline">Codes Promos</span>
            </TabsTrigger>
            <TabsTrigger value="credits" className="gap-2">
              <Gift className="w-4 h-4" />
              <span className="hidden sm:inline">Crédits</span>
            </TabsTrigger>
          </TabsList>

          {/* TIREO Settings Tab */}
          <TabsContent value="tireo">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Informations TIREO (Factures)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Raison sociale</Label>
                    <Input
                      value={tireoSettings.company_name}
                      onChange={(e) => setTireoSettings({ ...tireoSettings, company_name: e.target.value })}
                      placeholder="TIREO SAS"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email facturation</Label>
                    <Input
                      type="email"
                      value={tireoSettings.email}
                      onChange={(e) => setTireoSettings({ ...tireoSettings, email: e.target.value })}
                      placeholder="facturation@tireo.app"
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label>Adresse</Label>
                    <Input
                      value={tireoSettings.address}
                      onChange={(e) => setTireoSettings({ ...tireoSettings, address: e.target.value })}
                      placeholder="123 rue de la Chance"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Code postal</Label>
                    <Input
                      value={tireoSettings.postal_code}
                      onChange={(e) => setTireoSettings({ ...tireoSettings, postal_code: e.target.value })}
                      placeholder="75001"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ville</Label>
                    <Input
                      value={tireoSettings.city}
                      onChange={(e) => setTireoSettings({ ...tireoSettings, city: e.target.value })}
                      placeholder="Paris"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SIRET</Label>
                    <Input
                      value={tireoSettings.siret}
                      onChange={(e) => setTireoSettings({ ...tireoSettings, siret: e.target.value })}
                      placeholder="123 456 789 00012"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>N° TVA intracommunautaire</Label>
                    <Input
                      value={tireoSettings.tva_number}
                      onChange={(e) => setTireoSettings({ ...tireoSettings, tva_number: e.target.value })}
                      placeholder="FR12345678901"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Téléphone</Label>
                    <Input
                      value={tireoSettings.phone}
                      onChange={(e) => setTireoSettings({ ...tireoSettings, phone: e.target.value })}
                      placeholder="+33 1 23 45 67 89"
                    />
                  </div>
                </div>
                
                <div className="pt-4">
                  <Button onClick={saveTireoSettings} disabled={saving} className="gap-2">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Enregistrer
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promos">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  Codes Promos
                </CardTitle>
                <Button onClick={() => setShowPromoModal(true)} size="sm" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Nouveau code
                </Button>
              </CardHeader>
              <CardContent>
                {promoCodes.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Tag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucun code promo créé</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {promoCodes.map((code) => (
                      <div key={code.id} className="flex items-center justify-between p-4 rounded-lg border bg-card">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Tag className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-mono font-bold text-lg">{code.code}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              {code.discount_percent && (
                                <span className="flex items-center gap-1">
                                  <Percent className="w-3 h-3" />
                                  -{code.discount_percent}%
                                </span>
                              )}
                              {code.discount_amount && (
                                <span className="flex items-center gap-1">
                                  <Euro className="w-3 h-3" />
                                  -{code.discount_amount}€
                                </span>
                              )}
                              {code.free_operations && (
                                <span className="flex items-center gap-1">
                                  <Gift className="w-3 h-3" />
                                  +{code.free_operations} op.
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Hash className="w-3 h-3" />
                                {code.times_used || 0} utilisé(s)
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant={code.is_active ? "default" : "secondary"}>
                            {code.is_active ? "Actif" : "Inactif"}
                          </Badge>
                          <Switch
                            checked={code.is_active}
                            onCheckedChange={() => togglePromoCode(code.id, code.is_active)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Credits Tab */}
          <TabsContent value="credits">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  Attribuer des opérations gratuites
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                    <Input
                      placeholder="Rechercher un utilisateur (email ou entreprise)..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {filteredUsers.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Aucun utilisateur trouvé</p>
                      </div>
                    ) : (
                      filteredUsers.map((u) => (
                        <div key={u.id} className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <Users className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{u.company_name || "Sans nom"}</p>
                              <p className="text-sm text-muted-foreground">{u.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant={u.client_type === "association" ? "secondary" : "outline"}>
                              {u.client_type === "association" ? "Association" : "Entreprise"}
                            </Badge>
                            <Badge variant="outline">
                              {u.free_operations || 0} crédit(s)
                            </Badge>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedUser(u);
                                setShowCreditsModal(true);
                              }}
                            >
                              <Gift className="w-4 h-4 mr-1" />
                              Attribuer
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Promo Code Modal */}
      <Dialog open={showPromoModal} onOpenChange={setShowPromoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un code promo</DialogTitle>
            <DialogDescription>
              Ce code pourra être utilisé par les clients lors du paiement
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Code promo</Label>
              <Input
                value={newPromoCode.code}
                onChange={(e) => setNewPromoCode({ ...newPromoCode, code: e.target.value.toUpperCase() })}
                placeholder="BIENVENUE20"
                className="font-mono"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Type de réduction</Label>
              <Select
                value={newPromoCode.discount_type}
                onValueChange={(v) => setNewPromoCode({ ...newPromoCode, discount_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Pourcentage (%)</SelectItem>
                  <SelectItem value="amount">Montant fixe (€)</SelectItem>
                  <SelectItem value="operations">Opérations gratuites</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {newPromoCode.discount_type === "percent" && (
              <div className="space-y-2">
                <Label>Réduction (%)</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={newPromoCode.discount_percent}
                  onChange={(e) => setNewPromoCode({ ...newPromoCode, discount_percent: parseInt(e.target.value) })}
                />
              </div>
            )}
            
            {newPromoCode.discount_type === "amount" && (
              <div className="space-y-2">
                <Label>Montant (€)</Label>
                <Input
                  type="number"
                  min="1"
                  value={newPromoCode.discount_amount}
                  onChange={(e) => setNewPromoCode({ ...newPromoCode, discount_amount: parseFloat(e.target.value) })}
                />
              </div>
            )}
            
            {newPromoCode.discount_type === "operations" && (
              <div className="space-y-2">
                <Label>Nombre d'opérations gratuites</Label>
                <Input
                  type="number"
                  min="1"
                  value={newPromoCode.free_operations}
                  onChange={(e) => setNewPromoCode({ ...newPromoCode, free_operations: parseInt(e.target.value) })}
                />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date d'expiration (optionnel)</Label>
                <Input
                  type="date"
                  value={newPromoCode.valid_until}
                  onChange={(e) => setNewPromoCode({ ...newPromoCode, valid_until: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Utilisations max (optionnel)</Label>
                <Input
                  type="number"
                  min="1"
                  value={newPromoCode.max_uses}
                  onChange={(e) => setNewPromoCode({ ...newPromoCode, max_uses: e.target.value })}
                  placeholder="Illimité"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoModal(false)}>
              Annuler
            </Button>
            <Button onClick={createPromoCode}>
              Créer le code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Credits Modal */}
      <Dialog open={showCreditsModal} onOpenChange={setShowCreditsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attribuer des opérations gratuites</DialogTitle>
            <DialogDescription>
              {selectedUser && (
                <span>À : <strong>{selectedUser.company_name || selectedUser.email}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre d'opérations</Label>
              <Input
                type="number"
                min="1"
                value={creditsToGrant}
                onChange={(e) => setCreditsToGrant(parseInt(e.target.value) || 1)}
              />
            </div>
            <div className="space-y-2">
              <Label>Raison (optionnel)</Label>
              <Input
                value={creditsReason}
                onChange={(e) => setCreditsReason(e.target.value)}
                placeholder="Ex: Geste commercial, parrainage..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditsModal(false)}>
              Annuler
            </Button>
            <Button onClick={grantCredits}>
              Attribuer {creditsToGrant} opération(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
